import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../components/Card';
import CapabilitiesTable from '../../components/CapabilitiesTable';
import DeleteTenantModal from '../../components/DeleteTenantModal';
import PasswordResetRow from './PasswordResetRow';
import { fetchRolesCatalog } from '../../api/auth';
import DataExchangePage from '../DataExchangePage';
import DesignPage from '../DesignPage';
import TenantSettingsPage from '../TenantSettingsPage';
import '../SettingsHubPage.css';
import {
  listAdminTenants,
  createAdminTenant,
  updateAdminTenant,
  listAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  listAdminRoles,
  updateAdminRole,
  listAdminModules,
} from '../../api/admin';
import {
  ALL_MODULES,
  ALL_SCOPES,
  MODULE_LABEL_KEYS,
  SCOPE_LABEL_KEYS,
} from '../../nav/entitlements';
import './AdminHubPage.css';
import { ADMIN_SECTIONS } from '../../nav/adminSections';

/* Mot de passe généré : 12 caractères, au moins une majuscule, une minuscule,
   un chiffre et un symbole usuel (%!?+-*), tirés au sort cryptographique. */
const generatePassword = () => {
  const up = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const low = 'abcdefghijkmnpqrstuvwxyz';
  const dig = '23456789';
  const sym = '%!?+-*';
  const all = up + low + dig + sym;
  const pick = (set, n) => {
    const buf = new Uint32Array(n);
    window.crypto.getRandomValues(buf);
    return Array.from(buf, (x) => set[x % set.length]);
  };
  const chars = [...pick(up, 1), ...pick(low, 1), ...pick(dig, 1), ...pick(sym, 1), ...pick(all, 8)];
  // Mélange (Fisher-Yates) pour ne pas figer l'ordre maj/min/chiffre/symbole.
  const r = new Uint32Array(chars.length);
  window.crypto.getRandomValues(r);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = r[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

/* Champ mot de passe avec œil (voir/masquer) et générateur (⟳).
   C'est un formulaire de CRÉATION (l'admin pose le mot de passe d'un autre
   compte), pas une connexion : le champ reste un `type="text"` masqué par CSS
   (-webkit-text-security) pour que le navigateur et les gestionnaires de mots
   de passe ne proposent ni remplissage ni enregistrement — renforcé par les
   attributs d'exclusion Dashlane / 1Password / LastPass. */
function PasswordInput({ value, onChange, onGenerate, minLength = 8, required = true }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <span className="admin-pwd">
      <input
        type="text"
        className={show ? '' : 'is-masked'}
        name="np_field"
        value={value}
        onChange={onChange}
        minLength={minLength}
        required={required}
        autoComplete="off"
        data-form-type="other"
        data-1p-ignore="true"
        data-lpignore="true"
      />
      <button
        type="button" className="admin-pwd__btn"
        title={show ? t('admin:users.newUser.pwdHide', 'Masquer') : t('admin:users.newUser.pwdShow', 'Voir')}
        onClick={() => setShow((v) => !v)}
      >
        {show ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <path d="M14.12 14.12A3 3 0 1 1 9.88 9.88" /><path d="M1 1l22 22" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
      <button
        type="button" className="admin-pwd__btn"
        title={t('admin:users.newUser.pwdGenerate', 'Générer un mot de passe')}
        onClick={() => { onGenerate(generatePassword()); setShow(true); }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
          <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        </svg>
      </button>
    </span>
  );
}

const SCOPE_LEVEL_DEFS = [
  { value: '', labelKey: 'admin:scopeLevels.none' },
  { value: 'r', labelKey: 'admin:scopeLevels.read' },
  { value: 'rw', labelKey: 'admin:scopeLevels.readWrite' },
];

// Comptes admin plateforme protégés (break-glass) : toujours actifs, non
// supprimables. Miroir de PROTECTED_SUPERADMIN_EMAILS côté serveur (garde réelle).
const PROTECTED_SUPERADMIN_EMAILS = ['odoleans@gmail.com', 'olivier@myfinancepartner.ch'];
const isProtectedSuperadmin = (email) => PROTECTED_SUPERADMIN_EMAILS.includes(String(email || '').trim().toLowerCase());

function slugify(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 100);
}

function TenantsTab({ addToast, onTenantsChanged, onDeleteTenant, onSwitchTenant }) {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState([]);
  // Client visé par la modale de suppression. Distinct du client ACTIF : depuis
  // Administration on supprime n'importe quelle ligne sans basculer dessus.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [modules, setModules] = useState([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  // Formulaire de création replié par défaut (bouton en haut) : évite une grande
  // card et le scroll quand la liste de clients est longue.
  const [showCreate, setShowCreate] = useState(false);
  // Mot de passe du compte support (info@myfinancepartner.ch), généré UNE
  // SEULE fois côté serveur lors de sa toute première création (jamais relu
  // ensuite) : affiché ici tant que l'admin n'a pas fermé l'encart, pas dans
  // le toast qui s'auto-masque après 3,5 s.
  const [supportReveal, setSupportReveal] = useState(null);

  const reload = useCallback(async () => {
    const [tRes, mRes] = await Promise.all([listAdminTenants(), listAdminModules()]);
    if (tRes.data) setTenants(tRes.data);
    if (mRes.data) setModules(mRes.data);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    setBusy(true);
    const res = await createAdminTenant({
      name: name.trim(),
      slug: slug.trim(),
      currency: 'CHF',
      enabled_modules: [],
    });
    setBusy(false);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.creationFailed'), 'error');
      return;
    }
    addToast(t('admin:tenants.createdToast', { name: res.data.name }));
    if (res.support_account_password) {
      setSupportReveal({ tenantName: res.data.name, password: res.support_account_password });
    }
    setName('');
    setSlug('');
    setShowCreate(false);
    await reload();
    onTenantsChanged?.();
    // Bascule immédiate sur le client créé : l'intention (« je vais travailler
    // sur ce nouveau client ») et le contexte actif restent alignés — sans ça,
    // le geste suivant s'exécute sur le client PRÉCÉDENT (incident 2026-08-18).
    onSwitchTenant?.(res.data.id);
  }

  async function toggleModule(tenant, moduleId) {
    const current = [...(tenant.enabled_modules || [])];
    const next = current.includes(moduleId)
      ? current.filter((m) => m !== moduleId)
      : [...current, moduleId];
    // Pack toujours explicite : liste vide = aucun module (jamais null).
    const payload = { enabled_modules: next };
    const res = await updateAdminTenant(tenant.id, payload);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.updateFailed'), 'error');
      return;
    }
    setTenants((prev) => prev.map((tt) => (tt.id === tenant.id ? res.data : tt)));
    onTenantsChanged?.();
  }

  async function toggleAssistant(tenant) {
    const res = await updateAdminTenant(tenant.id, {
      assistant_enabled: !tenant.assistant_enabled,
    });
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.updateFailed'), 'error');
      return;
    }
    setTenants((prev) => prev.map((tt) => (tt.id === tenant.id ? res.data : tt)));
    onTenantsChanged?.();
  }

  // Résolu ICI, hors de la boucle sur les clients : dans `tenants.map((t) => …)`,
  // `t` désigne le client et masque la fonction de traduction — l'appeler dans la
  // boucle plante le rendu (page blanche sur l'onglet Clients).
  const assistantLabel = t('admin:tenants.assistant');
  const deleteLabel = t('admin:tenants.delete');

  const catalog = modules.length
    ? modules
    : ALL_MODULES.map((id) => ({ id, label: t(MODULE_LABEL_KEYS[id]) || id }));

  return (
    <>
      {!showCreate ? (
        <div className="admin-newtenant-bar">
          <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setShowCreate(true)}>
            ＋ {t('admin:tenants.newTenant.title')}
          </button>
        </div>
      ) : (
        <Card title={t('admin:tenants.newTenant.title')} subtitle={t('admin:tenants.newTenant.subtitle')}>
          <form className="admin-form" onSubmit={handleCreate}>
            <label>
              {t('admin:common.name')}
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setSlug(slugify(e.target.value));
                }}
                required
              />
            </label>
            <label>
              {t('admin:tenants.newTenant.slugLabel')}
              <input value={slug} onChange={(e) => setSlug(e.target.value)} required />
            </label>
            <button type="submit" className="admin-btn" disabled={busy}>{t('admin:common.create')}</button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={() => setShowCreate(false)}>{t('common:actions.cancel', 'Annuler')}</button>
          </form>
        </Card>
      )}

      {supportReveal && (
        <Card
          title={t('admin:tenants.supportAccount.title')}
          subtitle={t('admin:tenants.supportAccount.subtitle', { tenantName: supportReveal.tenantName })}
        >
          <div className="admin-pwd">
            <span className="admin-pwd__lbl">{t('admin:tenants.supportAccount.label')}</span>
            <code className="admin-mono">{supportReveal.password}</code>
            <button
              type="button"
              className="admin-btn-ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(supportReveal.password);
                  addToast(t('admin:tenants.supportAccount.copied'));
                } catch {
                  addToast(t('admin:tenants.supportAccount.copyFailed'), 'error');
                }
              }}
            >
              {t('admin:tenants.supportAccount.copy')}
            </button>
            <button type="button" className="admin-btn" onClick={() => setSupportReveal(null)}>
              {t('admin:tenants.supportAccount.done')}
            </button>
          </div>
          <p className="admin-pwd__note">{t('admin:tenants.supportAccount.note')}</p>
        </Card>
      )}

      <Card title={t('admin:tenants.listTitle')} flush>
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin:common.name')}</th>
              <th>{t('admin:common.slug')}</th>
              {catalog.map((m) => (
                <th key={m.id}>{m.label}</th>
              ))}
              <th>{assistantLabel}</th>
              <th className="admin-col-actions">{deleteLabel}</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => {
              const enabled = t.enabled_modules || [];
              return (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="admin-mono">{t.slug}</td>
                  {catalog.map((m) => (
                    <td key={m.id} className="admin-check">
                      <input
                        type="checkbox"
                        checked={enabled.includes(m.id)}
                        onChange={() => toggleModule(t, m.id)}
                        aria-label={`${t.name} — ${m.label}`}
                      />
                    </td>
                  ))}
                  <td className="admin-check">
                    <input
                      type="checkbox"
                      checked={Boolean(t.assistant_enabled)}
                      onChange={() => toggleAssistant(t)}
                      aria-label={`${t.name} — ${assistantLabel}`}
                    />
                  </td>
                  <td className="admin-col-actions">
                    <button
                      type="button"
                      className="admin-btn-delete"
                      onClick={() => setPendingDelete(t)}
                    >{deleteLabel}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {pendingDelete && (
        <DeleteTenantModal
          tenant={pendingDelete}
          addToast={addToast}
          onClose={() => setPendingDelete(null)}
          onConfirm={async () => {
            const ok = await onDeleteTenant?.(pendingDelete.id);
            setPendingDelete(null);
            // Supprimer le client ACTIF déconnecte (App) : le reload ne s'exécute
            // alors jamais, la page est déjà démontée.
            if (ok) await reload();
          }}
        />
      )}
    </>
  );
}

// Badges des périmètres accessibles (d'après les scopes d'un rôle). L+E surligné.
function ScopeBadges({ scopes }) {
  const { t } = useTranslation();
  const active = ALL_SCOPES.filter((s) => scopes && (scopes[s] === 'r' || scopes[s] === 'rw'));
  if (!active.length) return null;
  return (
    <span className="admin-scope-badges">
      {active.map((s) => (
        <span
          key={s}
          className={`admin-scope-badge${scopes[s] === 'rw' ? ' is-rw' : ''}`}
          title={t(scopes[s] === 'rw' ? 'admin:scopeLevels.readWrite' : 'admin:scopeLevels.read')}
        >
          {t(SCOPE_LABEL_KEYS[s])}
        </span>
      ))}
    </span>
  );
}

function UsersTab({ addToast, mode = 'clients', activeTenant = null }) {
      const { t } = useTranslation();
  // 'palestre' = utilisateurs admin plateforme (accès à tous les clients) ;
  // 'clients' = utilisateurs rattachés à des clients (rôle par appartenance).
  const isPalestre = mode === 'palestre';
const [tenants, setTenants] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterTenant, setFilterTenant] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    is_platform_admin: false,
    memberships: [{ tenant_id: '', role_id: '' }],
  });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  // Ligne dont on réinitialise le mot de passe (une seule à la fois).
  const [pwdFor, setPwdFor] = useState(null);

  const reload = useCallback(async () => {
    const [tRes, rRes, uRes] = await Promise.all([
      listAdminTenants(),
      listAdminRoles(),
      listAdminUsers(filterTenant || undefined),
    ]);
    if (tRes.data) setTenants(tRes.data);
    if (rRes.data) setRoles(rRes.data);
    if (uRes.data) setUsers(uRes.data);
  }, [filterTenant]);

  useEffect(() => { reload(); }, [reload]);

  async function handleCreate(e) {
    e.preventDefault();
    const memberships = form.memberships
      .filter((m) => m.tenant_id)
      .map((m) => ({ tenant_id: m.tenant_id, role_id: m.role_id || null }));
    if (!memberships.length || !form.email || !form.password || !form.name) return;
    setBusy(true);
    const res = await createAdminUser({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      memberships,
      is_platform_admin: form.is_platform_admin,
    });
    setBusy(false);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.creationFailed'), 'error');
      return;
    }
    addToast(t('admin:users.createdToast', { email: res.data.email }));
    setForm({
      name: '',
      email: '',
      password: '',
      is_platform_admin: false,
      memberships: [{ tenant_id: '', role_id: '' }],
    });
    await reload();
  }

  // Création d'un admin Palestre : accès à tous les clients, mais le modèle exige
  // un client d'atterrissage (point d'entrée, pas une restriction).
  async function handleCreatePalestre(e) {
    e.preventDefault();
    // Rattachement technique invisible (le modèle exige un tenant_id) : client
    // actif sinon le 1er. Aucune incidence — l'admin Palestre les voit tous.
    const landing = activeTenant?.id || tenants[0]?.id;
    if (!form.name || !form.email || !form.password || !landing) return;
    setBusy(true);
    const res = await createAdminUser({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      is_platform_admin: true,
      memberships: [{ tenant_id: landing, role_id: null }],
    });
    setBusy(false);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.creationFailed'), 'error');
      return;
    }
    addToast(t('admin:users.createdToast', { email: res.data.email }));
    setForm({ name: '', email: '', password: '', is_platform_admin: false, memberships: [{ tenant_id: '', role_id: '' }] });
    await reload();
  }

  async function patchUser(userId, payload) {
    const res = await updateAdminUser(userId, payload);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.updateFailed'), 'error');
      return;
    }
    setUsers((prev) => prev.map((u) => (u.id === userId ? res.data : u)));
  }

  async function deleteUserRow(u) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('admin:users.deleteConfirm', { name: u.name }))) return;
    const res = await deleteAdminUser(u.id);
    if (res.error) { addToast(res.error, 'error'); return; }
    setUsers((prev) => prev.filter((x) => x.id !== u.id));
    addToast(t('admin:users.deletedToast', { name: u.name }));
  }

  async function saveMemberships(user, draft) {
    const memberships = draft
      .filter((m) => m.tenant_id)
      .map((m) => ({ tenant_id: m.tenant_id, role_id: m.role_id || null }));
    if (!memberships.length) {
      addToast(t('admin:users.membershipRequired'), 'error');
      return;
    }
    await patchUser(user.id, { memberships });
    addToast(t('admin:users.membershipsSaved'));
  }

  return (
    <>
      {!isPalestre && (
      <Card title={t('admin:users.newUser.title')} subtitle={t('admin:users.newUser.subtitle')}>
        <form className="admin-form admin-form--grid" onSubmit={handleCreate} autoComplete="off" data-form-type="other">
          <label>
            {t('admin:common.name')}
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </label>
          <label>
            {t('admin:common.email')}
            <input
              type="email"
              name="nu_mail"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              autoComplete="off"
              data-form-type="other"
              data-1p-ignore="true"
              data-lpignore="true"
            />
          </label>
          <label>
            {t('admin:users.newUser.passwordLabel')}
            <PasswordInput
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              onGenerate={(pwd) => setForm((f) => ({ ...f, password: pwd }))}
            />
          </label>
          <div className="admin-form-span">
            <p className="admin-muted" style={{ marginBottom: '0.5rem' }}>{t('admin:users.newUser.tenantsAndRoles')}</p>
            <MembershipEditor
              value={form.memberships}
              onChange={(memberships) => setForm((f) => ({ ...f, memberships }))}
              tenants={tenants}
              roles={roles}
            />
          </div>
          <button type="submit" className="admin-btn" disabled={busy}>{t('admin:common.create')}</button>
        </form>
      </Card>
      )}

      {isPalestre && (
      <Card title={t('admin:users.palestreCreateTitle')} subtitle={t('admin:users.palestreCreateSubtitle')}>
        <form className="admin-form admin-form--grid" onSubmit={handleCreatePalestre} autoComplete="off" data-form-type="other">
          <label>
            {t('admin:common.name')}
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </label>
          <label>
            {t('admin:common.email')}
            <input type="email" name="nu_mail" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required
              autoComplete="off" data-form-type="other" data-1p-ignore="true" data-lpignore="true" />
          </label>
          <label>
            {t('admin:users.newUser.passwordLabel')}
            <PasswordInput
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              onGenerate={(pwd) => setForm((f) => ({ ...f, password: pwd }))}
            />
          </label>
          <button type="submit" className="admin-btn" disabled={busy}>{t('admin:users.palestreCreateBtn')}</button>
        </form>
      </Card>
      )}

      <Card
        title={isPalestre ? t('admin:users.palestreListTitle') : t('admin:users.listTitle')}
        actions={isPalestre ? undefined : (
          <select
            value={filterTenant}
            onChange={(e) => setFilterTenant(e.target.value)}
            aria-label={t('admin:users.filterAriaLabel')}
          >
            <option value="">{t('admin:users.allTenants')}</option>
            {tenants.map((tn) => (
              <option key={tn.id} value={tn.id}>{tn.name}</option>
            ))}
          </select>
        )}
        flush
      >
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin:common.name')}</th>
              <th>{t('admin:common.email')}</th>
              <th>{isPalestre ? t('admin:users.table.access') : t('admin:users.table.tenants')}</th>
              <th>{t('admin:users.table.active')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.filter((u) => (isPalestre ? u.is_platform_admin : !u.is_platform_admin)).map((u) => {
              const ms = u.memberships?.length
                ? u.memberships
                : [{ tenant_id: u.tenant_id, role_id: u.role_id, role: u.role }];
              const open = expanded === u.id;
              return (
                <React.Fragment key={u.id}>
                  <tr>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      {isPalestre ? (
                        <span className="admin-muted">{t('admin:users.allClientsAccess')}</span>
                      ) : (
                        ms.map((m, i) => {
                          const tn = tenants.find((x) => x.id === m.tenant_id);
                          const r = roles.find((x) => x.id === m.role_id);
                          return (
                            <div key={`${m.tenant_id}-${m.role_id || i}`} className="admin-membership-line">
                              <span className="admin-muted">
                                {(tn?.name || m.tenant_name || m.tenant_id)}
                                {' · '}
                                {(r?.name || m.role || '—')}
                              </span>
                              <ScopeBadges scopes={r?.scopes} />
                            </div>
                          );
                        })
                      )}
                    </td>
                    <td className="admin-check">
                      <input
                        type="checkbox"
                        checked={isProtectedSuperadmin(u.email) ? true : u.is_active}
                        disabled={isProtectedSuperadmin(u.email)}
                        title={isProtectedSuperadmin(u.email) ? t('admin:users.protectedTitle') : undefined}
                        onChange={(e) => patchUser(u.id, { is_active: e.target.checked })}
                      />
                    </td>
                    <td>
                      {isPalestre ? (
                        <div className="admin-row-actions">
                          {/* La réinitialisation vaut AUSSI pour un compte protégé :
                              c'est le recours quand l'exploitant perd son accès. */}
                          <button
                            type="button"
                            className="admin-btn-ghost"
                            onClick={() => setPwdFor(pwdFor === u.id ? null : u.id)}
                          >
                            {t('admin:users.password.action')}
                          </button>
                          {isProtectedSuperadmin(u.email)
                            ? <span className="admin-muted" title={t('admin:users.protectedTitle')}>{t('admin:users.protectedTag')}</span>
                            : <button type="button" className="admin-btn-delete" onClick={() => deleteUserRow(u)}>{t('admin:users.delete')}</button>}
                        </div>
                      ) : (
                        <div className="admin-row-actions">
                          <button
                            type="button"
                            className="admin-btn-ghost"
                            onClick={() => setExpanded(open ? null : u.id)}
                          >
                            {open ? t('admin:users.close') : t('admin:users.editTenants')}
                          </button>
                          <button
                            type="button"
                            className="admin-btn-ghost"
                            onClick={() => setPwdFor(pwdFor === u.id ? null : u.id)}
                          >
                            {t('admin:users.password.action')}
                          </button>
                          {/* Suppression définitive : le bouton n'existait que dans
                              l'onglet Palestre, alors que le serveur l'accepte
                              partout. Désactiver ≠ supprimer — les deux gestes ont
                              leur usage (départ vs erreur de saisie). */}
                          {isProtectedSuperadmin(u.email) ? (
                            <span className="admin-muted" title={t('admin:users.protectedTitle')}>
                              {t('admin:users.protectedTag')}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="admin-btn-delete"
                              onClick={() => deleteUserRow(u)}
                            >
                              {t('admin:users.delete')}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {pwdFor === u.id && (
                    <PasswordResetRow
                      user={u}
                      colSpan={5}
                      addToast={addToast}
                      onDone={() => setPwdFor(null)}
                    />
                  )}
                  {!isPalestre && open && (
                    <tr>
                      <td colSpan={5}>
                        <UserMembershipDraft
                          user={u}
                          tenants={tenants}
                          roles={roles}
                          onSave={(draft) => saveMemberships(u, draft)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Card>
    </>
  );
}

/**
 * Lignes client × rôle du formulaire de création.
 * ⚠️ Doit rester défini au niveau module : déclaré dans le corps de UsersTab,
 * il changeait d'identité à chaque frappe, React remontait le sous-arbre et le
 * champ en cours de saisie perdait le focus (une lettre par champ).
 */
function MembershipEditor({ value, onChange, tenants, roles }) {
      const { t } = useTranslation();
return (
    <div className="admin-memberships">
      {value.map((m, idx) => (
        <div key={idx} className="admin-membership-row">
          <select
            value={m.tenant_id}
            onChange={(e) => onChange(value.map((row, i) => (
              i === idx ? { ...row, tenant_id: e.target.value } : row
            )))}
          >
            <option value="">{t('admin:common.tenantPlaceholder')}</option>
            {tenants.map((tn) => (
              <option key={tn.id} value={tn.id}>{tn.name}</option>
            ))}
          </select>
          <select
            value={m.role_id || ''}
            onChange={(e) => onChange(value.map((row, i) => (
              i === idx ? { ...row, role_id: e.target.value } : row
            )))}
          >
            <option value="">{t('admin:common.rolePlaceholder')}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="admin-btn-ghost"
            disabled={value.length <= 1}
            title={value.length <= 1 ? t('admin:users.lastMembershipTitle') : undefined}
            onClick={() => onChange(value.filter((_, i) => i !== idx))}
          >
            {t('admin:common.remove')}
          </button>
        </div>
      ))}
      <button
        type="button"
        className="admin-btn-ghost"
        onClick={() => onChange([...value, { tenant_id: '', role_id: '' }])}
      >
        {t('admin:common.addTenant')}
      </button>
    </div>
  );
}

function UserMembershipDraft({ user, tenants, roles, onSave }) {
      const { t } = useTranslation();
const initial = (user.memberships?.length
    ? user.memberships
    : [{ tenant_id: user.tenant_id, role_id: user.role_id }]
  ).map((m) => ({ tenant_id: m.tenant_id, role_id: m.role_id || '' }));
  const [draft, setDraft] = useState(initial);

  return (
    <div className="admin-memberships admin-memberships--panel">
      {draft.map((m, idx) => (
        <div key={idx} className="admin-membership-row">
          <select
            value={m.tenant_id}
            onChange={(e) => setDraft((d) => d.map((row, i) => (
              i === idx ? { ...row, tenant_id: e.target.value } : row
            )))}
          >
            <option value="">{t('admin:common.tenantPlaceholder')}</option>
            {tenants.map((tn) => (
              <option key={tn.id} value={tn.id}>{tn.name}</option>
            ))}
          </select>
          <select
            value={m.role_id || ''}
            onChange={(e) => setDraft((d) => d.map((row, i) => (
              i === idx ? { ...row, role_id: e.target.value } : row
            )))}
          >
            <option value="">{t('admin:common.rolePlaceholder')}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="admin-btn-ghost"
            disabled={draft.length <= 1}
            title={draft.length <= 1 ? t('admin:users.lastMembershipTitle') : undefined}
            onClick={() => setDraft((d) => d.filter((_, i) => i !== idx))}
          >
            {t('admin:common.remove')}
          </button>
        </div>
      ))}
      <div className="admin-membership-actions">
        <button
          type="button"
          className="admin-btn-ghost"
          onClick={() => setDraft((d) => [...d, { tenant_id: '', role_id: '' }])}
        >
          {t('admin:common.addTenant')}
        </button>
        <button type="button" className="admin-btn" onClick={() => onSave(draft)}>
          {t('admin:common.save')}
        </button>
      </div>
    </div>
  );
}

// Ordre d'affichage des rôles (par slug). Les rôles hors liste (ex. lecteur)
// passent après, dans l'ordre de l'API.
const ROLE_ORDER = ['admin', 'full-access', 'ceo', 'finance', 'hr', 'sales', 'viewer'];
function roleRank(slug) { const i = ROLE_ORDER.indexOf(slug); return i === -1 ? 99 : i; }

function RolesTab({ addToast }) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  // Capacités : servies par /api/auth/roles, définies dans le code serveur.
  const [capabilities, setCapabilities] = useState([]);
  const scopeLevels = SCOPE_LEVEL_DEFS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));

  const reload = useCallback(async () => {
    const res = await listAdminRoles();
    if (res.data) setRoles(res.data);
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    let alive = true;
    fetchRolesCatalog().then((res) => {
      if (alive && res.capabilities) setCapabilities(res.capabilities);
    });
    return () => { alive = false; };
  }, []);

  const orderedRoles = [...roles].sort((a, b) => roleRank(a.slug) - roleRank(b.slug));

  async function saveRole(role, patch) {
    const res = await updateAdminRole(role.id, patch);
    if (res.error || !res.data) {
      addToast(res.error || t('admin:common.updateFailed'), 'error');
      return;
    }
    setRoles((prev) => prev.map((r) => (r.id === role.id ? res.data : r)));
    addToast(t('admin:roles.savedToast', { name: res.data.name }));
  }

  function setScope(role, scope, level) {
    const scopes = { ...(role.scopes || {}) };
    ALL_SCOPES.forEach((s) => {
      if (scopes[s] === undefined) scopes[s] = '';
    });
    scopes[scope] = level;
    saveRole(role, { scopes });
  }

  return (
    <>
      <Card
        title={t('admin:roles.title')}
        subtitle={t('admin:roles.subtitle')}
        flush
      >
      {/* Rôles en LIGNES, périmètres (scopes) en COLONNES. */}
      <table className="admin-table admin-roles-table">
        <thead>
          <tr>
            <th aria-hidden />
            <th className="admin-perimeter-head" colSpan={ALL_SCOPES.length}>{t('admin:roles.perimeterGroup', 'Périmètre')}</th>
          </tr>
          <tr>
            <th>{t('admin:roles.roleColumn', 'Rôle')}</th>
            {ALL_SCOPES.map((s) => (
              <th key={s}>{t(SCOPE_LABEL_KEYS[s]) || s}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orderedRoles.map((role) => (
            <tr key={role.id}>
              <td className="admin-scope-lbl" title={role.description || undefined}>
                <div className="admin-role-name">{role.name}</div>
                <div className="admin-mono">{role.slug}</div>
              </td>
              {ALL_SCOPES.map((s) => (
                <td key={s}>
                  <select
                    value={(role.scopes && role.scopes[s]) || ''}
                    onChange={(e) => setScope(role, s, e.target.value)}
                  >
                    {scopeLevels.map((opt) => (
                      <option key={opt.value || 'none'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </Card>

      <CapabilitiesTable capabilities={capabilities} roles={orderedRoles} />
    </>
  );
}

/* Administration — hub affiché comme les autres modules : ses sections vivent dans
   le panneau contextuel de la Sidebar (prop `activeSection`, pilotée par App).
   Cette page ne rend que le contenu. Réservé à l'admin plateforme (isPlatformAdmin,
   vérifié dans App). */
export default function AdminHubPage({
  addToast, onTenantsChanged, activeTenant,
  onTenantPatched, onRenameTenant, onDeleteTenant, activeSection, onSwitchTenant,
}) {
  const { t } = useTranslation();
  const ids = ADMIN_SECTIONS.map((s) => s.id);
  const section = ids.includes(activeSection) ? activeSection : ids[0];
  // Données clients : le client désigné en tête de section, et la liste des clients.
  const [targetId, setTargetId] = useState('');
  const [targets, setTargets] = useState([]);
  const refreshTargets = useCallback(() => {
    listAdminTenants().then((res) => { if (res.data) setTargets(res.data); });
  }, []);
  useEffect(() => { if (section === 'client') refreshTargets(); }, [section, refreshTargets]);

  const renderSection = (id) => {
    switch (id) {
      case 'tenants': return <TenantsTab addToast={addToast} onTenantsChanged={onTenantsChanged} onDeleteTenant={onDeleteTenant} onSwitchTenant={onSwitchTenant} />;
      case 'palestre': return <UsersTab addToast={addToast} mode="palestre" activeTenant={activeTenant} />;
      case 'users':   return <UsersTab addToast={addToast} mode="clients" />;
      case 'roles':   return <RolesTab addToast={addToast} />;
      case 'design':  return <DesignPage embedded />;
      /* DONNÉES CLIENTS (2026-10-11) : tout ce qui porte sur UN client, dans
         l'ordre : échange Excel, sauvegarde JSON (client actif), puis
         restauration, renommage et suppression, sur le client DÉSIGNÉ
         (incident 2026-08-18). */
      case 'client':  return (
        <>
          <DataExchangePage activeTenant={activeTenant} addToast={addToast} />
          <TenantSettingsPage
            activeTenant={activeTenant} onTenantPatched={onTenantPatched}
            onRenameTenant={onRenameTenant} onDeleteTenant={onDeleteTenant}
            addToast={addToast} embedded sections={['backup']}
          />
          <TenantSettingsPage
            activeTenant={activeTenant} onTenantPatched={onTenantPatched}
            onRenameTenant={onRenameTenant} onDeleteTenant={onDeleteTenant}
            addToast={addToast} embedded sections={['restore', 'rename', 'delete']}
            pickedTarget={targets.find((x) => x.id === targetId) || null}
            onTargetGone={() => { setTargetId(''); refreshTargets(); }}
          />
        </>
      );
      default: return null;
    }
  };

  return (
    <div className="settings-page">
      <div className={`settings-page__content admin-hub${section === 'client' ? ' admin-hub--client' : ''}`}>
        {/* Portée de la section : elle opère sur UN client, il se nomme en tête
            (incident 2026-08-18 : données chargées sur le mauvais client faute
            de voir sur qui on opérait). */}
        {section === 'client' && (
          <h1 className="admin-ghead">
            {t('nav:adminClient')} <strong>{activeTenant?.name || '?'}</strong>
          </h1>
        )}
        {/* Le CLIENT CIBLE en tête de section (demande d'Olivier, 2026-10-11) :
            il gouverne la restauration, le renommage et la suppression. Vide
            par défaut, aucun contexte hérité (incident 2026-08-18). */}
        {section === 'client' && (
          <div className="settings-target-bar">
            <span className="settings-target-lbl">{t('settings:restore.targetTitle', 'Client cible')}</span>
            <select className="settings-target-select" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">{t('settings:restore.targetPlaceholder', '— Désigner le client —')}</option>
              {targets.map((tn) => <option key={tn.id} value={tn.id}>{tn.name}</option>)}
            </select>
          </div>
        )}
        {renderSection(section)}
      </div>
    </div>
  );
}
